import type { DfsGraphPath, DfsCollectGraphPaths } from "./types";

/**
 * Collects all root-to-leaf paths in a directed graph using depth-first search.
 * Root nodes are nodes with no incoming edges.
 * Leaf nodes are nodes with no outgoing edges.
 *
 * @returns An array of paths, where each path is an ordered sequence of {x, y} coordinates.
 */
export const dfsCollectGraphPaths: DfsCollectGraphPaths = ({ nodes, edges }) => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  const adjacency = new Map<number, number[]>();
  for (const edge of edges) {
    if (!adjacency.has(edge.from)) adjacency.set(edge.from, []);
    adjacency.get(edge.from)!.push(edge.to);
  }

  const allToNodes = new Set(edges.map((e) => e.to));
  const startNodes = nodes.filter((n) => !allToNodes.has(n.id));

  const collectedPaths: DfsGraphPath[] = [];

  const dfs = (currentId: number, currentPath: DfsGraphPath): void => {
    const node = nodeMap.get(currentId);
    if (!node) return;

    const newPath: DfsGraphPath = [...currentPath, { x: node.x, y: node.y }];
    const nextNodes = adjacency.get(currentId);

    if (!nextNodes || nextNodes.length === 0) {
      collectedPaths.push(newPath);
      return;
    }

    for (const nextId of nextNodes) {
      dfs(nextId, newPath);
    }
  };

  for (const startNode of startNodes) {
    dfs(startNode.id, []);
  }

  return collectedPaths;
};
